import {Dialog} from '@reach/dialog';
import {DialogFooter, DialogTitle} from '@common/components/Dialog';
import {
  Input,
  FormItem,
} from '@common/components/form-items';
import {Controller, useForm, useFieldArray, get} from 'react-hook-form';
import Button, {ButtonGroup, IconButton} from '@common/components/Button';
import Select from '@common/components/Select';
import {defaultBasicSubjectCodes} from '@gakki/api/types';
import {DevTool} from '@hookform/devtools';
import React, { useState, useEffect, HTMLAttributes } from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPlusCircle} from '@fortawesome/pro-light-svg-icons';
import InfoTip from '@gakki/components/info-tip/info-tip';
import {ReactComponent as TrashIcon} from 'app/gakki/assets/trash.svg';
import SystemSalarySubjectDialog from './system-salary-subject-dialog';
import {
  DeleteDialog,
} from '@gakki/components/Dialog';
const SubjectError: React.FC<HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div
      className={`text-xs w-full left-0 bottom-[-17px] text-[#ff3b30] ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

function SalarySubjectForm(props: any) {
  const {
    isOpen,
    onClose,
    onConfirm,
    defaultValues,
    options,
    getEmployeeData,
  } = props;
  const {register, reset, control, watch, handleSubmit, errors, formState} = useForm();
  const systemSubjectCode = options.systemSalarySubject.map((s: any) => s.code);
  const [employeeUsedSubject, setEmployeeUsedSubject] = useState<any[]>([])
  const [showSystemSubjectDialog, setShowSystemSubjectDialog] = useState(false)
  const [deleteIdx, setDeleteIdx] = useState<number | null>(null);
  const [isInit, setIsInit] = useState(false)
  const subjectFieldName = 'salarySubjects';
  const subjectField = useFieldArray({
    control: control,
    name: subjectFieldName,
    keyName: 'subjectFormId',
  });

  useEffect(() => {
    if (isOpen && !isInit && defaultValues.length > 0) {
      reset({ salarySubjects: defaultValues })
      const employeeData = getEmployeeData();
      const _employeeUsedSubject: any = new Set(employeeData.map((employee: any) => {
        return employee.payrollSubject?.payrollSubjectItems?.map((s: any) => s.salaryAccountId);
      }).flat())
      setEmployeeUsedSubject([..._employeeUsedSubject].filter(s => s))
      setIsInit(true)
    }
    if (!isOpen) {
      setIsInit(false)
    }
  }, [isOpen, defaultValues, isInit])

  const toggleSystemSubjectDialog = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setShowSystemSubjectDialog(prev => !prev)
  }

  const handleDeleteSubject = () => {
    if (typeof deleteIdx === 'number') {
      subjectField.remove(deleteIdx);
      setDeleteIdx(null);
    }
  }
  return (
    <Dialog isOpen={isOpen} style={{width: 650}} aria-label="salary-subject-dialog">
      <DialogTitle
        title={
          <span>
            薪資科目設定
            <InfoTip info={"薪資科目的計算規則，請在啟用後另行設定"}/>
          </span>
        }
        onClose={onClose}
      />
      <form className={'subject-form'} onSubmit={handleSubmit(onConfirm)}>
        <div
          className={
            'flex flex-col border-b border-b-[#e9eced] mb-5 pt-5 pb-10 gap-2.5'
          }
        >
          <div className="bg-[#eff0f0] rounded-[2px] p-2.5 mb-2.5">
            <div>* 自訂薪資科目若已使用，則無法刪除。</div>
            <div>* 「自訂薪資科目代碼」不能與「<a className="text-brand cursor-pointer hover:underline" onClick={toggleSystemSubjectDialog}>系統預設薪資科目代碼</a>」重複。</div>
          </div>
          {
            subjectField.fields.map((f: any, index: number) => {
              const isCategoryError = formState.isSubmitted && get(
                errors,
                `${subjectFieldName}.${index}.salaryAccountCategoryId`,
              )
              const isAccountCodeError = formState.isSubmitted && get(errors, `${subjectFieldName}.${index}.salaryAccountCode`)
              const isAccountNameError = formState.isSubmitted && get(errors, `${subjectFieldName}.${index}.salaryAccountName`)
              return (
                <div className="flex gap-2.5" key={f.subjectFormId}>
                  <input
                    name={`${subjectFieldName}.${index}.hnSalaryAccountCode`}
                    hidden
                    ref={register()}
                    defaultValue={f.hnSalaryAccountCode}
                  />
                  <input
                    name={`${subjectFieldName}.${index}.hnSalaryAccountName`}
                    hidden
                    ref={register()}
                    defaultValue={f.hnSalaryAccountName}
                  />
                  <input
                    name={`${subjectFieldName}.${index}.salaryAccountId`}
                    hidden
                    ref={register()}
                    defaultValue={f.salaryAccountId}
                  />
                  <FormItem
                    label="主類別"
                    itemName={`subject-form-${index}-category`}
                    required
                    width="130px"
                    data-test-id={`subject-form-${index}-category`}
                  >
                    <Controller
                      render={({onChange, value}) => (
                        <Select
                          value={options.accountCategory.find((o: any) => o.id === value)}
                          options={options.accountCategory}
                          onChange={(o: any) => {
                            onChange(o.id);
                          }}
                          getOptionLabel={(o: any) => `${o.name}`}
                          getOptionValue={(o: any) => o.id}
                          error={Boolean(isCategoryError)}
                          isDisabled={f.hnSalaryAccountCode}
                          placeholder={"請選擇主類別"}
                          filterOption={(option: any) => {
                            return /001|0011/.test(option.data.code); // 只能選擇主類別為基本薪資、固定津貼
                          }}
                        />
                      )}
                      name={`${subjectFieldName}.${index}.salaryAccountCategoryId`}
                      control={control}
                      rules={{required: '主類別為必填'}}
                      defaultValue={f.salaryAccountCategoryId}
                    />
                    {isCategoryError && (
                      <SubjectError>
                        {
                          get(
                            errors,
                            `${subjectFieldName}.${index}.salaryAccountCategoryId`,
                          )?.message
                        }
                      </SubjectError>
                    )}
                  </FormItem>
                  <FormItem
                    label="科目代碼"
                    itemName={`subject-form-${index}-code`}
                    info={f.hnSalaryAccountCode ? `系統預設科目代碼為 ${f.hnSalaryAccountCode}` : undefined}
                    required
                    width="100px"
                    data-test-id={`subject-form-${index}-code`}
                  >
                    <Controller
                      render={({onChange, value}) => (
                        <Input
                          style={{boxSizing: 'border-box', width: '100%' }}
                          value={value}
                          disabled={false}
                          onChange={e => {
                            const {value} = e.target;
                            onChange(value);
                          }}
                          maxLength={6}
                          error={isAccountCodeError}
                          placeholder="字數限制6字"
                        />
                      )}
                      name={`${subjectFieldName}.${index}.salaryAccountCode`}
                      control={control}
                      rules={{
                        required: '科目代碼為必填',
                        validate: {
                          duplicatesWithSystemSubjectCode: v =>
                          systemSubjectCode.includes(v)
                              ? '不可與系統預設科目代碼重複'
                              : true,
                          duplicates: v => {
                            const updateSubjectFieldValue = watch(subjectFieldName).map((s: any, i: number) => {
                              if (i === index) {
                                s.salaryAccountCode = v
                              }
                              return s
                            })
                            return updateSubjectFieldValue.filter((s: any) => v === s.salaryAccountCode).length > 1
                              ? '科目代碼不可重複'
                              : true
                          },
                        },
                      }}
                      defaultValue={f.salaryAccountCode}
                    />
                    {isAccountCodeError && (
                      <SubjectError>
                        {
                          get(
                            errors,
                            `${subjectFieldName}.${index}.salaryAccountCode`,
                          )?.message
                        }
                      </SubjectError>
                    )}
                  </FormItem>
                  <FormItem
                    label="科目名稱"
                    itemName={`subject-form-${index}-name`}
                    info={f.hnSalaryAccountName ? `系統預設科目名稱為 ${f.hnSalaryAccountName}` : undefined}
                    required
                    width="335px"
                    data-test-id={`subject-form-${index}-name`}
                  >
                    <Controller
                      render={({onChange, value}) => (
                        <Input
                          style={{boxSizing: 'border-box', width: '100%' }}
                          value={value}
                          disabled={false}
                          onChange={e => {
                            const {value} = e.target;
                            onChange(value);
                          }}
                          maxLength={30}
                          error={isAccountNameError}
                          placeholder="字數限制30字"
                        />
                      )}
                      name={`${subjectFieldName}.${index}.salaryAccountName`}
                      control={control}
                      rules={{
                        required: '科目名稱為必填',
                      }}
                      defaultValue={f.salaryAccountName}
                    />
                    {isAccountNameError && (
                      <SubjectError>
                        {
                          get(
                            errors,
                            `${subjectFieldName}.${index}.salaryAccountName`,
                          )?.message
                        }
                      </SubjectError>
                    )}
                  </FormItem>
                  <div
                    data-test-id={`subject-form-${index}-delete`}
                    style={{
                      display:
                        defaultBasicSubjectCodes.includes(f.hnSalaryAccountCode)
                          ? 'none'
                          : 'block',
                      paddingTop: '33px',
                    }}
                  >
                    <IconButton
                      className="pr-0"
                      onClick={e => {
                        e.preventDefault();
                        if (employeeUsedSubject.includes(f.salaryAccountId)) {
                          setDeleteIdx(index);
                        } else {
                          subjectField.remove(index);
                        }
                      }}
                      disabled={false}
                      data-test-id={'remove-btn'}
                      type="button"
                    >
                      <TrashIcon/>
                    </IconButton>
                  </div>
                </div>
              )
            })
          }
          <div className="h-5 flex">
            <IconButton
              className="pl-0"
              onClick={() => {
                subjectField.append({
                  salaryAccountCategoryId: null,
                  hnSalaryAccountCode: null,
                  hnSalaryAccountName: null,
                  salaryAccountId: null,
                  salaryAccountCode: "",
                  salaryAccountName: ""
                });
              }}
              disabled={false}
              data-test-id={'add-btn'}
              type="button"
            >
              <FontAwesomeIcon icon={faPlusCircle} style={{fontSize: 20}} />
              新增薪資科目
            </IconButton>
          </div>
        </div>
        <DialogFooter>
          <ButtonGroup direction={'rtl'}>
            <Button type={'submit'} variant={'cta'} className={'confirm'}>
              確定
            </Button>
            <Button variant={'primary'} className={'close'} onClick={onClose}>
              取消
            </Button>
          </ButtonGroup>
        </DialogFooter>
      </form>
      <DevTool control={control}/>
      <DeleteDialog
        text={'刪除此科目後，將從所有人員的個人薪資科目內移除，是否確定刪除？'}
        isOpen={typeof deleteIdx === 'number'}
        onCancel={() => setDeleteIdx(null)}
        onConfirm={handleDeleteSubject}
      />
      <SystemSalarySubjectDialog
        isOpen={showSystemSubjectDialog}
        onClose={toggleSystemSubjectDialog}
        salarySubjects={options.systemSalarySubject}
      />
    </Dialog>
  );
}

export default SalarySubjectForm;
